import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Settings2 } from 'lucide-react';
import { appointmentService } from '../../services/appointmentService';
import { useLanguage } from '../../hooks/useLanguage';
import { WeekAgenda, DayConfig } from './WeekAgenda';
import { AgendaSidebar } from './AgendaSidebar';
import { AppointmentViewer } from './AppointmentViewer';
import { NewAppointmentModal } from './NewAppointmentModal';
import { AvailabilitySettings } from './AvailabilitySettings';
import { ACTIVE_STATUSES, Appointment, addDays, errorCode, formatDay, startOfWeek, todayKey } from './appointmentUtils';

type Tab = 'agenda' | 'availability';

// Page « Agenda » : semaine, colonne « RDV à valider », onglet Disponibilités
export const AppointmentsPage: React.FC = () => {
    const { t, currentLanguage, isRTL } = useLanguage();
    const [params, setParams] = useSearchParams();
    const [tab, setTab] = useState<Tab>(params.get('tab') === 'availability' ? 'availability' : 'agenda');
    const [weekStart, setWeekStart] = useState(startOfWeek(todayKey()));
    const [week, setWeek] = useState<Appointment[]>([]);
    const [open, setOpen] = useState<Appointment[]>([]);
    const [summary, setSummary] = useState<{ count: number; occupancyPercent: number } | null>(null);
    const [cfg, setCfg] = useState<any>(null);
    const [selected, setSelected] = useState<{ a: Appointment; counter?: boolean } | null>(null);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        try {
            const to = addDays(weekStart, 6);
            const [w, o, c, st] = await Promise.all([
                appointmentService.getAppointments({ from: weekStart, to, limit: 200 }),
                appointmentService.getAppointments({ status: 'REQUESTED,COUNTER_PROPOSED,CHANGE_REQUESTED,CONFIRMED', limit: 200 }),
                appointmentService.getConfig(),
                appointmentService.getStats({ from: weekStart, to })
            ]);
            setWeek(w.data || []); setOpen(o.data || []); setCfg(c.data);
            setSummary(st.data?.period || null);
            setError('');
        } catch (err) {
            setError(t(`appointments.errors.${errorCode(err)}`, { defaultValue: t('appointments.errors.generic') }));
        }
    }, [weekStart, t]);

    useEffect(() => { load(); }, [load]);

    // Ouverture directe depuis une notification ou un message : /dashboard/appointments?id=...
    useEffect(() => {
        const id = params.get('id');
        if (!id) return;
        appointmentService.getAppointmentById(id).then(r => setSelected({ a: r.data })).catch(() => undefined);
        params.delete('id'); setParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const busyInspectionIds = useMemo(() => open.filter(a => ACTIVE_STATUSES.includes(a.status) && a.inspectionId).map(a => String(a.inspectionId)), [open]);
    const lunchOf = (a: Appointment): [string, string] | null => {
        const d = cfg?.schedule?.[['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'][new Date(a.date).getUTCDay()]];
        return d?.lunchStart && d?.lunchEnd ? [d.lunchStart, d.lunchEnd] : null;
    };
    const closures = (cfg?.closures || []).map((c: any) => ({ from: String(c.from).slice(0, 10), to: String(c.to).slice(0, 10), label: c.label }));

    const quickAccept = async (a: Appointment) => {
        try { await appointmentService.respond(a._id, { action: 'ACCEPT' }); load(); }
        catch (err) { setError(t(`appointments.errors.${errorCode(err)}`, { defaultValue: t('appointments.errors.generic') })); }
    };

    return (
        <div className="space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold text-gray-900">{t('appointments.title')}</h1>
                    <p className="text-sm text-gray-500 first-letter:uppercase">{formatDay(todayKey(), currentLanguage, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => setTab(tab === 'agenda' ? 'availability' : 'agenda')} className="inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl border border-gray-300 bg-white text-gray-800 text-sm font-semibold hover:bg-gray-50">
                        <Settings2 size={16} /><span className="hidden sm:inline">{tab === 'agenda' ? t('appointments.tabs.availability') : t('appointments.tabs.agenda')}</span>
                    </button>
                    <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 shadow-sm">
                        <Plus size={16} /><span>{t('appointments.new')}</span>
                    </button>
                </div>
            </div>

            {error && <p role="alert" className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

            {tab === 'agenda' ? (
                <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px] items-start">
                    <WeekAgenda appointments={week} weekStart={weekStart} schedule={cfg?.schedule as Record<string, DayConfig> | undefined} closures={closures} summary={summary}
                                onPrev={() => setWeekStart(addDays(weekStart, -7))} onNext={() => setWeekStart(addDays(weekStart, 7))}
                                onToday={() => setWeekStart(startOfWeek(todayKey()))} onSelect={a => setSelected({ a })} />
                    <AgendaSidebar appointments={open} onOpen={a => setSelected({ a })} onQuickAccept={quickAccept} onOtherSlot={a => setSelected({ a, counter: true })} />
                </div>
            ) : (
                <AvailabilitySettings onSaved={c => { setCfg(c); load(); }} />
            )}

            {selected && <AppointmentViewer appointment={selected.a} counterFirst={selected.counter} lunch={lunchOf(selected.a)} onClose={() => setSelected(null)} onChanged={load} />}
            {creating && <NewAppointmentModal busyInspectionIds={busyInspectionIds} defaultDuration={cfg?.defaultDuration || 60} onClose={() => setCreating(false)} onCreated={load} />}
        </div>
    );
};
